/**
 * Responsive Design System — Mobile-First Architecture
 * 
 * Provides responsive utilities, safe area handling, and adaptive layouts
 * for native mobile applications comparable to PayPal, Revolut, Airbnb, Uber.
 */

import { useWindowDimensions, Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

/* ──────────────────────────────────────────────────────────────────── */
/* RESPONSIVE UTILITIES                                                 */
/* ──────────────────────────────────────────────────────────────────── */

export enum ScreenSize {
  SMALL = 'small',    // < 400px (small phones)
  MEDIUM = 'medium',  // 400-600px (standard phones)
  LARGE = 'large',    // 600-900px (tablets, large phones)
  XLARGE = 'xlarge',  // > 900px (large tablets)
}

export const useResponsive = () => {
  const dimensions = useWindowDimensions();
  const insets = useSafeAreaInsets();
  
  const getScreenSize = (): ScreenSize => {
    const { width } = dimensions;
    if (width < 400) return ScreenSize.SMALL;
    if (width < 600) return ScreenSize.MEDIUM;
    if (width < 900) return ScreenSize.LARGE;
    return ScreenSize.XLARGE;
  };

  const screenSize = getScreenSize();
  const isSmall = screenSize === ScreenSize.SMALL;
  const isMedium = screenSize === ScreenSize.MEDIUM;
  const isLarge = screenSize === ScreenSize.LARGE;
  const isXLarge = screenSize === ScreenSize.XLARGE;
  const isTablet = isLarge || isXLarge;
  const isMobile = !isTablet;

  return {
    width: dimensions.width,
    height: dimensions.height,
    screenSize,
    isSmall,
    isMedium,
    isLarge,
    isXLarge,
    isTablet,
    isMobile,
    insets,
    isLandscape: dimensions.width > dimensions.height,
    isPortrait: dimensions.height > dimensions.width,
  };
};

/* ──────────────────────────────────────────────────────────────────── */
/* SPACING SCALE (4px base unit)                                        */
/* ──────────────────────────────────────────────────────────────────── */

export const spacing = {
  xs: 4,      // 4px
  sm: 8,      // 8px
  md: 12,     // 12px
  lg: 16,     // 16px
  xl: 24,     // 24px
  xxl: 32,    // 32px
  xxxl: 48,   // 48px
  huge: 64,   // 64px
} as const;

/* ──────────────────────────────────────────────────────────────────── */
/* RESPONSIVE SPACING (adaptive based on screen size)                   */
/* ──────────────────────────────────────────────────────────────────── */

export const getResponsiveSpacing = (screenSize: ScreenSize) => {
  switch (screenSize) {
    case ScreenSize.SMALL:
      return {
        screenPadding: spacing.md,     // 12px
        sectionSpacing: spacing.lg,    // 16px
        cardGap: spacing.md,           // 12px
      };
    case ScreenSize.MEDIUM:
      return {
        screenPadding: spacing.lg,     // 16px
        sectionSpacing: spacing.xl,    // 24px
        cardGap: spacing.lg,           // 16px
      };
    case ScreenSize.LARGE:
      return {
        screenPadding: spacing.xl,     // 24px
        sectionSpacing: spacing.xxl,   // 32px
        cardGap: spacing.xl,           // 24px
      };
    case ScreenSize.XLARGE:
      return {
        screenPadding: spacing.xxl,    // 32px
        sectionSpacing: spacing.xxxl,  // 48px
        cardGap: spacing.xxl,          // 32px
      };
  }
};

/* ──────────────────────────────────────────────────────────────────── */
/* BORDER RADIUS (standardized)                                         */
/* ──────────────────────────────────────────────────────────────────── */

export const borderRadius = {
  sm: 8,      // Small elements, buttons
  md: 12,     // Cards, modals
  lg: 16,     // Large containers
  xl: 20,     // Extra large
  full: 9999, // Circular
} as const;

/* ──────────────────────────────────────────────────────────────────── */
/* ELEVATION / SHADOWS (Material Design 3)                              */
/* ──────────────────────────────────────────────────────────────────── */

export const shadows = {
  none: {
    shadowColor: 'transparent',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
    elevation: 0,
  },
  xs: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  sm: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    elevation: 2,
  },
  md: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.12,
    shadowRadius: 8,
    elevation: 4,
  },
  lg: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
  },
  xl: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.2,
    shadowRadius: 16,
    elevation: 12,
  },
} as const;

/* ──────────────────────────────────────────────────────────────────── */
/* TYPOGRAPHY (standardized font sizes)                                 */
/* ──────────────────────────────────────────────────────────────────── */

export const typography = {
  // Headers
  h1: {
    fontSize: 28,
    lineHeight: 36,
    fontWeight: '800' as const,
  },
  h2: {
    fontSize: 24,
    lineHeight: 32,
    fontWeight: '800' as const,
  },
  h3: {
    fontSize: 20,
    lineHeight: 28,
    fontWeight: '700' as const,
  },
  h4: {
    fontSize: 18,
    lineHeight: 26,
    fontWeight: '700' as const,
  },
  
  // Body
  bodyLarge: {
    fontSize: 16,
    lineHeight: 24,
    fontWeight: '400' as const,
  },
  body: {
    fontSize: 14,
    lineHeight: 22,
    fontWeight: '400' as const,
  },
  bodySmall: {
    fontSize: 13,
    lineHeight: 20,
    fontWeight: '400' as const,
  },
  
  // Caption
  caption: {
    fontSize: 12,
    lineHeight: 18,
    fontWeight: '500' as const,
  },
  captionSmall: {
    fontSize: 11,
    lineHeight: 16,
    fontWeight: '500' as const,
  },
  
  // Labels
  label: {
    fontSize: 12,
    lineHeight: 16,
    fontWeight: '600' as const,
  },
  labelSmall: {
    fontSize: 11,
    lineHeight: 14,
    fontWeight: '600' as const,
  },
} as const;

/* ──────────────────────────────────────────────────────────────────── */
/* TOUCH TARGET SIZES (minimum 48x48 for accessibility)                */
/* ──────────────────────────────────────────────────────────────────── */

export const touchTarget = {
  small: 40,   // 40x40 (icon buttons)
  normal: 48,  // 48x48 (standard buttons)
  large: 56,   // 56x56 (primary buttons)
  hero: 64,    // 64x64 (large CTAs)
} as const;

/* ──────────────────────────────────────────────────────────────────── */
/* SAFE AREA HOOK (iOS notch + Android status bar handling)             */
/* ──────────────────────────────────────────────────────────────────── */

export const useSafeAreaPadding = () => {
  const insets = useSafeAreaInsets();
  
  return {
    paddingTop: insets.top,
    paddingBottom: insets.bottom,
    paddingLeft: insets.left,
    paddingRight: insets.right,
    topInset: insets.top,
    bottomInset: insets.bottom,
  };
};

/* ──────────────────────────────────────────────────────────────────── */
/* ANIMATION TIMINGS                                                    */
/* ──────────────────────────────────────────────────────────────────── */

export const animations = {
  fast: 150,
  normal: 300,
  slow: 500,
} as const;

/* ──────────────────────────────────────────────────────────────────── */
/* CONTAINER WIDTHS (max-width for readable content)                    */
/* ──────────────────────────────────────────────────────────────────── */

export const containerWidth = {
  max: 600, // Comfortable reading width on tablets
} as const;
